import { JSX } from 'react'
import type { AddTownCompanyRequestStatus } from '@common/models/project/add-town-company-request.modl'

/**
 * 事業所追加依頼のステータスを正規化
 */
const normalizeRequestStatus = (value: string): AddTownCompanyRequestStatus | null => {
  switch (value) {
    case 'pending':
    case 'in_progress':
    case 'completed':
    case 'cancelled':
      return value
    case '未対応':
      return 'pending'
    case '対応中':
      return 'in_progress'
    case '完了':
      return 'completed'
    case 'キャンセル':
      return 'cancelled'
    case 'cancel':
      return 'cancelled'
    default:
      return null
  }
}

/**
 * 事業所追加依頼のステータスをパース
 */
export const parseAddTownCompanyRequestStatus = (value: string): AddTownCompanyRequestStatus | null => {
  return normalizeRequestStatus(value)
}

/**
 * 事業所追加依頼のステータスを日本語ラベルに変換
 */
export const getAddTownCompanyRequestStatusLabel = (status: AddTownCompanyRequestStatus | string): string => {
  const normalized = normalizeRequestStatus(status)
  switch (normalized) {
    case 'pending':
      return '未対応'
    case 'in_progress':
      return '生成中'
    case 'completed':
      return '完了'
    case 'cancelled':
      return 'キャンセル'
    default:
      return status
  }
}

interface AddTownCompanyRequestStatusBadgeProps {
  status: AddTownCompanyRequestStatus | string
}

/**
 * 事業所追加依頼ステータスバッジコンポーネント
 * 事業所追加依頼のステータスをバッジ形式で表示
 */
export const AddTownCompanyRequestStatusBadge = ({ status }: AddTownCompanyRequestStatusBadgeProps): JSX.Element => {
  const normalized = normalizeRequestStatus(status)

  const getStatusStyle = () => {
    switch (normalized) {
      case 'pending':
        // 未対応 - グレー系
        return {
          backgroundColor: '#f5f5f5',
          color: '#666',
        }
      case 'in_progress':
        // 対応中 - オレンジ系（進行中）
        return {
          backgroundColor: '#fff3e0',
          color: '#e65100',
        }
      case 'completed':
        // 完了 - 緑系（完了）
        return {
          backgroundColor: '#e8f5e9',
          color: '#2e7d32',
        }
      case 'cancelled':
        // キャンセル - 赤系
        return {
          backgroundColor: '#ffebee',
          color: '#c62828',
        }
      default:
        return {
          backgroundColor: '#f5f5f5',
          color: '#666',
        }
    }
  }

  const style = getStatusStyle()

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'normal',
        lineHeight: '1.2',
        backgroundColor: style.backgroundColor,
        color: style.color,
        whiteSpace: 'nowrap',
      }}
    >
      {getAddTownCompanyRequestStatusLabel(status)}
    </span>
  )
}
